// scenes/produits/index.jsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  InputAdornment,
  Chip,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Badge,
  
  Card,
  CardContent,
  LinearProgress,
  Tabs,
  Tab,
  Avatar,
  AvatarGroup,
  Select,
  FormControl,
  InputLabel,
} from '@mui/material';
// MUI v7 : le `Grid` par défaut a perdu les props `item` / `xs` / `md`
// au profit de `size={{...}}`. Ce fichier utilise l'ancienne API.
import { GridLegacy as Grid } from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  Inventory as InventoryIcon,
  Category as CategoryIcon,
  AttachMoney as MoneyIcon,
  ShoppingCart as ShoppingCartIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Visibility as VisibilityIcon,
  FilterList as FilterListIcon,
  Clear as ClearIcon,
  Download as DownloadIcon,
  Print as PrintIcon,
  Upload as UploadIcon,
  BarChart as BarChartIcon,
  History as HistoryIcon,
  QrCode as QrCodeIcon,
  LocalOffer as LocalOfferIcon,
  Numbers as NumbersIcon,
  Image as ImageIcon,
  Warehouse as WarehouseIcon,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { produitService } from 'services/produitService';
import { categorieService } from 'services/categorieService';
import ProduitForm from './ProduitForm';
import StockDialog from './StockDialog';

const Produits = () => {
  const theme = useTheme();
  const [produits, setProduits] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [activeTab, setActiveTab] = useState(0);
  
  const [openForm, setOpenForm] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openStockDialog, setOpenStockDialog] = useState(false);
  const [openQRDialog, setOpenQRDialog] = useState(false);
  const [selectedProduit, setSelectedProduit] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedActionProduit, setSelectedActionProduit] = useState(null);

  const [filters, setFilters] = useState({
    categorie: '',
    est_actif: null,
    stock: 'all',
  });

  const [stats, setStats] = useState({
    total: 0,
    actifs: 0,
    enRupture: 0,
    stockFaible: 0,
    valeurStock: 0,
  });

  const loadProduits = async () => {
    try {
      setLoading(true);
      setError('');
      const params = {
        page: page + 1,
        page_size: rowsPerPage,
        search: searchTerm || undefined,
        ...(filters.categorie && { categorie: filters.categorie }),
        ...(filters.est_actif !== null && { est_actif: filters.est_actif }),
      };
      const response = await produitService.getAllProduits(params);
      if (response.results) {
        setProduits(response.results);
        setTotalItems(response.count || 0);
      } else {
        setProduits(response);
        setTotalItems(response.length);
      }
      calculateStats(response.results || response);
    } catch (err) {
      setError(err.message || 'Erreur lors du chargement des produits');
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      setLoadingCategories(true);
      const response = await categorieService.getAllCategories();
      setCategories(Array.isArray(response) ? response : response.results || []);
    } catch (err) {
      console.error('Erreur de chargement des catégories:', err);
    } finally {
      setLoadingCategories(false);
    }
  };

  const calculateStats = (produitsList) => {
    const total = produitsList.length;
    const actifs = produitsList.filter(p => p.est_actif).length;
    const enRupture = produitsList.filter(p => p.stock_actuel <= 0).length;
    const stockFaible = produitsList.filter(p => p.stock_actuel > 0 && p.stock_actuel <= p.stock_minimum).length;
    const valeurStock = produitsList.reduce((sum, p) => sum + (p.stock_actuel * (p.prix_achat || 0)), 0);
    setStats({ total, actifs, enRupture, stockFaible, valeurStock });
  };

  useEffect(() => { loadProduits(); loadCategories(); }, [page, rowsPerPage, searchTerm, filters]);

  useEffect(() => {
    if (activeTab === 1) loadEnRupture();
    else if (activeTab === 2) loadStockFaible();
  }, [activeTab]);

  const loadEnRupture = async () => {
    try { setLoading(true); const r = await produitService.getEnRupture(); setProduits(r); setTotalItems(r.length); }
    catch (err) { console.error(err); } finally { setLoading(false); }
  };

  const loadStockFaible = async () => {
    try { setLoading(true); const r = await produitService.getStockFaible(); setProduits(r); setTotalItems(r.length); }
    catch (err) { console.error(err); } finally { setLoading(false); }
  };

  const handleSearch = (e) => { setSearchTerm(e.target.value); setPage(0); };
  const clearSearch = () => { setSearchTerm(''); setPage(0); };
  const handleChangePage = (e, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); };
  const handleFilterChange = (name, value) => { setFilters(prev => ({ ...prev, [name]: value })); setPage(0); };
  const clearFilters = () => { setFilters({ categorie: '', est_actif: null, stock: 'all' }); setPage(0); };

  const handleOpenForm = (produit = null) => { setSelectedProduit(produit); setIsEditing(!!produit); setOpenForm(true); };
  const handleCloseForm = () => { setOpenForm(false); setSelectedProduit(null); };
  const handleSaveSuccess = () => { handleCloseForm(); loadProduits(); };

  const handleOpenDeleteDialog = (produit) => { setSelectedProduit(produit); setOpenDeleteDialog(true); };
  const handleCloseDeleteDialog = () => { setOpenDeleteDialog(false); setSelectedProduit(null); };
  const handleDelete = async () => {
    try { await produitService.deleteProduit(selectedProduit.id); handleCloseDeleteDialog(); loadProduits(); }
    catch (err) { setError(err.message || 'Erreur lors de la suppression'); }
  };

  const handleOpenStockDialog = (produit) => { setSelectedProduit(produit); setOpenStockDialog(true); };
  const handleCloseStockDialog = () => { setOpenStockDialog(false); setSelectedProduit(null); };
  const handleOpenQRDialog = (produit) => { setSelectedProduit(produit); setOpenQRDialog(true); };
  const handleCloseQRDialog = () => { setOpenQRDialog(false); setSelectedProduit(null); };

  const handleActionMenuOpen = (e, produit) => { setAnchorEl(e.currentTarget); setSelectedActionProduit(produit); };
  const handleActionMenuClose = () => { setAnchorEl(null); setSelectedActionProduit(null); };

  const handleToggleActivation = async (produit) => {
    try { await produitService.toggleActivation(produit.id, !produit.est_actif); handleActionMenuClose(); loadProduits(); }
    catch (err) { setError(err.message || 'Erreur lors du changement de statut'); }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const formatPrix = (prix) => {
    if (!prix) return '0,00';
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'HTG', minimumFractionDigits: 2 }).format(prix);
  };

  const tabs = [
    { label: 'Tous les produits', value: 'all' },
    { label: 'En rupture', value: 'rupture', icon: <WarningIcon />, count: stats.enRupture },
    { label: 'Stock faible', value: 'faible', icon: <WarningIcon />, count: stats.stockFaible },
    { label: 'Actifs', value: 'actifs', icon: <CheckCircleIcon /> },
  ];

  // Style pour le label au-dessus de la valeur dans les cellules
  const CellWithLabel = ({ label, children }) => (
    <Box>
      <Typography variant="caption" color="textSecondary" display="block" lineHeight={1.2} mb={0.3}>
        {label}
      </Typography>
      {children}
    </Box>
  );

  return (
    <Box m="20px">
      {/* En-tête */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
        <Box display="flex" alignItems="center" gap={2}>
          <InventoryIcon sx={{ fontSize: 40, color: theme.palette.primary.main }} />
          <Box>
            <Typography variant="h3" fontWeight="bold">Gestion des Produits</Typography>
            <Typography variant="body1" color="textSecondary">Gérez l'inventaire de votre entreprise</Typography>
          </Box>
        </Box>
        <Box display="flex" gap={2}>
          <Button variant="outlined" startIcon={<UploadIcon />} onClick={() => document.getElementById('import-file').click()}>
            Importer
          </Button>
          <input id="import-file" type="file" accept=".xlsx,.xls,.csv" style={{ display: 'none' }}
            onChange={async (e) => {
              const file = e.target.files[0];
              if (file) {
                try { await produitService.importProduits(file); loadProduits(); }
                catch (err) { setError('Erreur lors de l\'import: ' + err.message); }
              }
            }}
          />
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenForm()}
            sx={{ backgroundColor: theme.palette.primary.main, '&:hover': { backgroundColor: theme.palette.primary.dark } }}>
            Nouveau Produit
          </Button>
        </Box>
      </Box>

      {/* Recherche et filtres */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField fullWidth variant="outlined" placeholder="Rechercher par nom, code, description..."
              value={searchTerm} onChange={handleSearch}
              InputProps={{
                startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>,
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton onClick={clearSearch} size="small"><ClearIcon /></IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Catégorie</InputLabel>
              <Select value={filters.categorie} onChange={(e) => handleFilterChange('categorie', e.target.value)} label="Catégorie" disabled={loadingCategories}>
                <MenuItem value="">Toutes les catégories</MenuItem>
                {categories.map((c) => <MenuItem key={c.id} value={c.id}>{c.nom}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Statut</InputLabel>
              <Select value={filters.est_actif === null ? '' : filters.est_actif.toString()} onChange={(e) => handleFilterChange('est_actif', e.target.value === '' ? null : e.target.value === 'true')} label="Statut">
                <MenuItem value="">Tous les statuts</MenuItem>
                <MenuItem value="true">Actifs</MenuItem>
                <MenuItem value="false">Inactifs</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <Button fullWidth variant="outlined" startIcon={<ClearIcon />} onClick={clearFilters} disabled={!filters.categorie && filters.est_actif === null}>
              Effacer filtres
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Statistiques */}
      <Grid container spacing={3} mb={3}>
        {[
          { value: stats.total, label: 'Produits', icon: <InventoryIcon />, color: theme.palette.primary.light },
          { value: stats.actifs, label: 'Actifs', icon: <CheckCircleIcon />, color: theme.palette.success.light, textColor: 'success.main' },
          { value: stats.enRupture, label: 'En rupture', icon: <WarningIcon />, color: theme.palette.error.light, textColor: 'error.main' },
          { value: stats.stockFaible, label: 'Stock faible', icon: <WarningIcon />, color: theme.palette.warning.light, textColor: 'warning.main' },
          { value: formatPrix(stats.valeurStock), label: 'Valeur stock', icon: <MoneyIcon />, color: theme.palette.info.light, textColor: 'info.main' },
        ].map((stat, i) => (
          <Grid item xs={12} sm={6} md={2.4} key={i}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" gap={2}>
                  <Avatar sx={{ bgcolor: stat.color }}>{stat.icon}</Avatar>
                  <Box>
                    <Typography variant="h4" fontWeight="bold" color={stat.textColor}>{stat.value}</Typography>
                    <Typography variant="body2" color="textSecondary">{stat.label}</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} variant="scrollable" scrollButtons="auto">
          {tabs.map((tab, i) => (
            <Tab key={i} label={
              <Box display="flex" alignItems="center" gap={1}>
                {tab.icon}
                {tab.label}
                {tab.count !== undefined && (
                  <Chip label={tab.count} size="small" color={i === 1 ? 'error' : i === 2 ? 'warning' : 'default'} variant="outlined" />
                )}
              </Box>
            } />
          ))}
        </Tabs>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>{error}</Alert>}

      {/* ── Tableau — Stock et Prix supprimés, label au-dessus de chaque valeur ── */}
      <Paper sx={{ width: '100%', overflow: 'hidden', mb: 4 }}>
        <TableContainer sx={{ maxHeight: 600 }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                {/* Les labels dans le header sont retirés puisqu'ils apparaissent dans chaque cellule */}
                <TableCell sx={{ fontWeight: 700 }}>Image</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Produit</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Catégorie</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Statut</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Dernière mise à jour</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 8 }}>
                    <CircularProgress />
                    <Typography variant="body2" sx={{ mt: 2 }}>Chargement des produits...</Typography>
                  </TableCell>
                </TableRow>
              ) : produits.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 8 }}>
                    <InventoryIcon sx={{ fontSize: 60, color: 'text.disabled', mb: 2 }} />
                    <Typography variant="h6" color="textSecondary">Aucun produit trouvé</Typography>
                    <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                      {searchTerm || filters.categorie || filters.est_actif !== null
                        ? 'Aucun résultat pour votre recherche'
                        : 'Commencez par créer votre premier produit'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                produits.map((produit) => (
                  <TableRow key={produit.id} hover>

                    {/* Image */}
                    <TableCell>
                      <CellWithLabel label="Image">
                        {produit.image ? (
                          <Box component="img" src={produit.image} alt={produit.nom}
                            sx={{ width: 50, height: 50, borderRadius: 1, objectFit: 'cover', border: `1px solid ${theme.palette.divider}` }} />
                        ) : (
                          <Box sx={{ width: 50, height: 50, borderRadius: 1, backgroundColor: theme.palette.grey[200], display: 'flex', alignItems: 'center', justifyContent: 'center', border: `1px solid ${theme.palette.divider}` }}>
                            <ImageIcon sx={{ color: theme.palette.grey[500] }} />
                          </Box>
                        )}
                      </CellWithLabel>
                    </TableCell>

                    {/* Produit */}
                    <TableCell>
                      <CellWithLabel label="Produit">
                        <Typography fontWeight="medium">{produit.nom}</Typography>
                        <Typography variant="body2" color="textSecondary">Code: {produit.code || 'N/A'}</Typography>
                        {produit.description && (
                          <Typography variant="body2" color="textSecondary"
                            sx={{ overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 1, WebkitBoxOrient: 'vertical', maxWidth: 200 }}>
                            {produit.description}
                          </Typography>
                        )}
                      </CellWithLabel>
                    </TableCell>

                    {/* Catégorie */}
                    <TableCell>
                      <CellWithLabel label="Catégorie">
                        {produit.categorie ? (
                          <Chip label={produit.categorie_nom} size="small" icon={<CategoryIcon />} variant="outlined" />
                        ) : (
                          <Typography variant="body2" color="textSecondary">Non catégorisé</Typography>
                        )}
                      </CellWithLabel>
                    </TableCell>

                    {/* Statut */}
                    <TableCell>
                      <CellWithLabel label="Statut">
                        <Chip
                          icon={produit.est_actif ? <CheckCircleIcon /> : <CancelIcon />}
                          label={produit.est_actif ? 'Actif' : 'Inactif'}
                          color={produit.est_actif ? 'success' : 'error'}
                          size="small"
                          variant="outlined"
                        />
                      </CellWithLabel>
                    </TableCell>

                    {/* Dernière mise à jour */}
                    <TableCell>
                      <CellWithLabel label="Dernière mise à jour">
                        <Typography variant="body2">{formatDate(produit.date_modification)}</Typography>
                      </CellWithLabel>
                    </TableCell>

                    {/* Actions */}
                    <TableCell align="center">
                      
                        <Box display="flex" justifyContent="center" gap={1}>
                          <Tooltip title="Modifier">
                            <IconButton color="primary" onClick={() => handleOpenForm(produit)} size="small">
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Plus d'options">
                            <IconButton onClick={(e) => handleActionMenuOpen(e, produit)} size="small">
                              <MoreVertIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                     
                    </TableCell>

                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={totalItems}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Lignes par page:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} sur ${count}`}
        />
      </Paper>

      {/* Menu d'actions contextuel */}
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleActionMenuClose}>
        {selectedActionProduit && (
          <>
            <MenuItem onClick={() => handleToggleActivation(selectedActionProduit)}>
              <ListItemIcon>
                {selectedActionProduit.est_actif
                  ? <CancelIcon fontSize="small" color="error" />
                  : <CheckCircleIcon fontSize="small" color="success" />}
              </ListItemIcon>
              <ListItemText>{selectedActionProduit.est_actif ? 'Désactiver' : 'Activer'}</ListItemText>
            </MenuItem>
            <MenuItem onClick={() => { handleActionMenuClose(); handleOpenDeleteDialog(selectedActionProduit); }}>
              <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
              <ListItemText sx={{ color: 'error.main' }}>Supprimer</ListItemText>
            </MenuItem>
          </>
        )}
      </Menu>

      {/* Modal formulaire */}
      <ProduitForm open={openForm} onClose={handleCloseForm} onSuccess={handleSaveSuccess} produit={selectedProduit} isEditing={isEditing} categories={categories} />

      {/* Modal stock */}
      <StockDialog open={openStockDialog} onClose={handleCloseStockDialog} produit={selectedProduit} onSuccess={() => { handleCloseStockDialog(); loadProduits(); }} />

      {/* Modal QR Code */}
      <Dialog open={openQRDialog} onClose={handleCloseQRDialog} maxWidth="xs" fullWidth>
        <DialogTitle>QR Code du produit</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 4 }}>
          {selectedProduit && (
            <>
              <Box sx={{ p: 2, border: `2px solid ${theme.palette.divider}`, borderRadius: 2, mb: 3 }} />
              <Typography variant="h6" align="center">{selectedProduit.nom}</Typography>
              <Typography variant="body2" color="textSecondary" align="center">Code: {selectedProduit.code}</Typography>
              <Typography variant="body1" fontWeight="bold" align="center" sx={{ mt: 1 }}>{formatPrix(selectedProduit.prix_vente)}</Typography>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseQRDialog}>Fermer</Button>
          <Button variant="contained" onClick={() => window.print()}>Imprimer</Button>
        </DialogActions>
      </Dialog>

      {/* Modal suppression */}
      <Dialog open={openDeleteDialog} onClose={handleCloseDeleteDialog} maxWidth="xs" fullWidth>
        <DialogTitle>Confirmer la suppression</DialogTitle>
        <DialogContent>
          <Typography>Êtes-vous sûr de vouloir supprimer le produit "{selectedProduit?.nom}" ?</Typography>
          <Typography variant="body2" color="error" sx={{ mt: 2 }}>
            Attention: Cette action est irréversible. Tous les mouvements de stock associés seront également supprimés.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>Annuler</Button>
          <Button onClick={handleDelete} color="error" variant="contained" startIcon={<DeleteIcon />}>Supprimer</Button>
        </DialogActions>
      </Dialog>

      {/* Export */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mt={4}>
        <Typography variant="body2" color="textSecondary">{totalItems} produit(s) au total</Typography>
        <Box display="flex" gap={2}>
          <Button variant="outlined" startIcon={<PrintIcon />} onClick={() => window.print()}>Imprimer</Button>
          <Button variant="outlined" startIcon={<DownloadIcon />} onClick={async () => {
            try {
              const blob = await produitService.exportProduits();
              const url = window.URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = `produits_${new Date().toISOString().split('T')[0]}.xlsx`;
              document.body.appendChild(a);
              a.click();
              window.URL.revokeObjectURL(url);
              document.body.removeChild(a);
            } catch (err) { setError('Erreur lors de l\'export: ' + err.message); }
          }}>
            Exporter Excel
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default Produits;
